import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { createPluReportDownload, getPluReport } from '../../../../APICalling/APICalling';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import Loader from '../../../../Components/Loader/Loader';
import Placeholder from '../../../../Components/Placeholder/Placeholder';
import SearchNotFoundIcon from '../../../../Components/Icons/SearchNotFoundIcon';
import ReportHeader from '../ReportHeader/ReportHeader';
import { calculateReportDateRange, getPeriodText, isReportRangeWithinMonths } from '../../../../Utils/ReportDateUtils';
import { downloadFileWithAuth, getDownloadFilename } from '../../../../Utils/downloadFileWithAuth';
import { attachSharedUserChannelListener } from '../../../../Utils/clearSharedUserChannelEvent';
import logger from '../../../../Utils/logger';
import styles from './PluReport.module.css';

const toYmd = (apiDate) => {
    if (!apiDate) return '';
    const datePart = String(apiDate).split(' ')[0];
    const parts = datePart.split('-');
    if (parts.length !== 3) return datePart;
    if (parts[0].length === 2 && parts[2].length === 4) {
        const [day, month, year] = parts;
        return `${year}-${month}-${day}`;
    }
    return datePart;
};

const getYesterdayYmd = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const year = yesterday.getFullYear();
    const month = String(yesterday.getMonth() + 1).padStart(2, '0');
    const day = String(yesterday.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const clampToYesterday = (ymd) => {
    if (!ymd) return ymd;
    const max = getYesterdayYmd();
    return ymd > max ? max : ymd;
};

const getClampedDateRange = (filterParams) => {
    const { startDate, endDate } = calculateReportDateRange(filterParams);
    let startYmd = clampToYesterday(toYmd(startDate));
    let endYmd = clampToYesterday(toYmd(endDate));
    if (startYmd && endYmd && startYmd > endYmd) {
        startYmd = endYmd;
    }
    return { startYmd, endYmd };
};

// Same wording as the API's data_lag_notice, used until the API has been called.
const getDefaultDataLagNotice = () => {
    const today = new Date();
    const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    const formatShort = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `Data is updated daily. Figures for ${formatShort(today)} will appear on ${formatShort(tomorrow)}.`;
};

const formatMoney = (value) => {
    const num = parseFloat(value || 0);
    if (Number.isNaN(num)) return '0.00';
    return num.toFixed(2);
};

const formatQty = (value) => {
    const num = Number(value ?? 0);
    return Number.isNaN(num) ? 0 : num;
};

const sumTotals = (list = []) =>
    list.reduce(
        (acc, cat) => {
            const t = cat?.total || {};
            acc.sold += formatQty(t.sold);
            acc.item_sales += parseFloat(t.item_sales || 0) || 0;
            acc.total_discount += parseFloat(t.total_discount || 0) || 0;
            acc.foc_amount += parseFloat(t.foc_amount || 0) || 0;
            acc.revenue += parseFloat(t.revenue || 0) || 0;
            acc.refund_qty += formatQty(t.refund_qty);
            acc.refund_amount += parseFloat(t.refund_amount || 0) || 0;
            acc.nett_sold_qty += formatQty(t.nett_sold_qty);
            return acc;
        },
        {
            sold: 0,
            item_sales: 0,
            total_discount: 0,
            foc_amount: 0,
            revenue: 0,
            refund_qty: 0,
            refund_amount: 0,
            nett_sold_qty: 0,
        }
    );

const emptyGiftTotals = {
    quantity: 0,
    returned_quantity: 0,
    nett_quantity: 0,
};

const sumGiftTotals = (list = []) =>
    list.reduce(
        (acc, gift) => {
            acc.quantity += formatQty(gift?.quantity);
            acc.returned_quantity += formatQty(gift?.returned_quantity);
            acc.nett_quantity += formatQty(gift?.nett_quantity);
            return acc;
        },
        { ...emptyGiftTotals }
    );

const PluReport = ({ filterParams }) => {
    const { selectedOutlet, userAssignedOutlets } = useGlobalContext();
    const [categories, setCategories] = useState([]);
    const [grandTotal, setGrandTotal] = useState(null);
    const [membershipGifts, setMembershipGifts] = useState([]);
    const [membershipGiftsTotal, setMembershipGiftsTotal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [outletName, setOutletName] = useState('');
    const [periodText, setPeriodText] = useState('');
    const [dataLagNotice, setDataLagNotice] = useState('');

    useEffect(() => {
        if (selectedOutlet && userAssignedOutlets?.length) {
            const outlet = userAssignedOutlets.find((o) => o.id === selectedOutlet);
            setOutletName(outlet?.name || 'Unknown');
        }
    }, [selectedOutlet, userAssignedOutlets]);

    const fetchPluReport = useCallback(async () => {
        if (!filterParams || !filterParams.date) {
            setCategories([]);
            setGrandTotal(null);
            setMembershipGifts([]);
            setMembershipGiftsTotal(null);
            return;
        }

        setLoading(true);
        try {
            const { startYmd, endYmd } = getClampedDateRange(filterParams);
            const params = {
                start_date: startYmd,
                end_date: endYmd,
                outlet_id: filterParams.outlet || selectedOutlet,
            };

            if (filterParams.main_category) {
                params.main_category = String(filterParams.main_category);
            }

            setPeriodText(getPeriodText(filterParams));

            const response = await getPluReport(params);
            const isSuccess = response?.data?.success ?? response?.success ?? false;

            if (isSuccess) {
                const payload = response?.data?.data || {};
                const gifts = payload.membership_gifts || {};
                setCategories(Array.isArray(payload.categories) ? payload.categories : []);
                setGrandTotal(payload.grand_total || null);
                setMembershipGifts(Array.isArray(gifts.summary) ? gifts.summary : []);
                setMembershipGiftsTotal(gifts.summary_total || null);
                if (payload.data_lag_notice) {
                    setDataLagNotice(payload.data_lag_notice);
                }
            } else {
                setCategories([]);
                setGrandTotal(null);
                setMembershipGifts([]);
                setMembershipGiftsTotal(null);
                toast.error(
                    response?.response?.data?.message ||
                        response?.data?.message ||
                        response?.message ||
                        'Failed to fetch PLU report'
                );
            }
        } catch (error) {
            console.error('Error fetching PLU report:', error);
            setCategories([]);
            setGrandTotal(null);
            setMembershipGifts([]);
            setMembershipGiftsTotal(null);
            toast.error(
                error?.response?.data?.message ||
                    error?.message ||
                    'An error occurred while fetching PLU report'
            );
        } finally {
            setLoading(false);
        }
    }, [filterParams, selectedOutlet]);

    const hasGenerated = !!filterParams?.generated;

    useEffect(() => {
        if (!hasGenerated) {
            setCategories([]);
            setGrandTotal(null);
            setMembershipGifts([]);
            setMembershipGiftsTotal(null);
            setPeriodText('-');
            return;
        }
        if (filterParams.date && filterParams.outlet === selectedOutlet) {
            fetchPluReport();
        }
    }, [filterParams, fetchPluReport, selectedOutlet, hasGenerated]);

    const gTotal = useMemo(() => {
        if (grandTotal) {
            return {
                sold: formatQty(grandTotal.sold),
                item_sales: parseFloat(grandTotal.item_sales || 0) || 0,
                total_discount: parseFloat(grandTotal.total_discount || 0) || 0,
                foc_amount: parseFloat(grandTotal.foc_amount || 0) || 0,
                revenue: parseFloat(grandTotal.revenue || 0) || 0,
                refund_qty: formatQty(grandTotal.refund_qty),
                refund_amount: parseFloat(grandTotal.refund_amount || 0) || 0,
                nett_sold_qty: formatQty(grandTotal.nett_sold_qty),
            };
        }
        return sumTotals(categories);
    }, [grandTotal, categories]);

    const mainCategoryLabel = filterParams?.main_category
        ? String(filterParams.main_category)
        : 'All';

    const hasData = categories.length > 0;
    const hasGifts = membershipGifts.length > 0;

    const giftTotals = useMemo(() => {
        if (membershipGiftsTotal) {
            return {
                quantity: formatQty(membershipGiftsTotal.quantity),
                returned_quantity: formatQty(membershipGiftsTotal.returned_quantity),
                nett_quantity: formatQty(membershipGiftsTotal.nett_quantity),
            };
        }
        return sumGiftTotals(membershipGifts);
    }, [membershipGifts, membershipGiftsTotal]);

    const renderMetricCells = (row) => {
        const sold = formatQty(row?.sold);
        const itemSales = row?.item_sales;
        const totDisc = row?.total_discount;
        const foc = row?.foc_amount;
        const revenue = row?.revenue;
        const refundQty = formatQty(row?.refund_qty);
        const refundAmount = row?.refund_amount;
        const nettSoldQty = formatQty(row?.nett_sold_qty);

        return (
            <>
                <td className={`${styles.colSold} ${styles.rightAlign}`}>{sold}</td>
                <td className={`${styles.colMoney} ${styles.rightAlign}`}>{formatMoney(itemSales)}</td>
                <td className={`${styles.colMoney} ${styles.rightAlign}`}>{formatMoney(totDisc)}</td>
                <td className={`${styles.colMoney} ${styles.rightAlign}`}>{formatMoney(foc)}</td>
                <td className={`${styles.colMoney} ${styles.rightAlign}`}>{formatMoney(revenue)}</td>
                <td className={`${styles.colSold} ${styles.rightAlign}`}>{refundQty}</td>
                <td className={`${styles.colMoney} ${styles.rightAlign}`}>{formatMoney(refundAmount)}</td>
                <td className={`${styles.colSold} ${styles.rightAlign}`}>{nettSoldQty}</td>
            </>
        );
    };

    const handleDownloadCSV = async () => {
        if (!hasData) {
            toast.warning('No data available to download');
            return;
        }
        if (!isReportRangeWithinMonths(filterParams, 1)) {
            toast.error('Please select a date range within 1 month to proceed.');
            return;
        }

        const userId = localStorage.getItem('user_id');
        const token = localStorage.getItem('token');

        if (!userId) {
            toast.error('User ID not found');
            return;
        }
        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        const { startYmd, endYmd } = getClampedDateRange(filterParams);
        const channelName = `user.${userId}`;
        const eventName = 'PluReportReady';
        let detach = null;
        let toastId = null;

        setDownloadLoading(true);
        toastId = toast.loading('Downloading in progress...');

        const cleanup = () => {
            try {
                detach?.();
            } catch (e) {}
            if (toastId) {
                toast.dismiss(toastId);
                toastId = null;
            }
        };

        try {
            const { channel, detach: detachListener } = attachSharedUserChannelListener(
                channelName,
                eventName,
                async (event) => {
                    try {
                        const downloadUrl = event?.url || event?.data?.url;
                        if (!downloadUrl) {
                            toast.error('Download URL not received. Please try again.');
                            cleanup();
                            setDownloadLoading(false);
                            return;
                        }

                        try {
                            await downloadFileWithAuth(
                                downloadUrl,
                                getDownloadFilename(event, downloadUrl, 'plu-report.csv')
                            );
                            toast.success('Downloaded successfully');
                        } catch (downloadError) {
                            logger.error('PLU download failed:', downloadError);
                            toast.error('Failed to start download');
                        }

                        cleanup();
                        setDownloadLoading(false);
                    } catch (error) {
                        toast.error('Failed to process download');
                        cleanup();
                        setDownloadLoading(false);
                    }
                }
            );
            detach = detachListener;

            if (typeof channel.error === 'function') {
                channel.error(() => {
                    toast.error('Socket connection error');
                    cleanup();
                    setDownloadLoading(false);
                });
            }

            await new Promise((resolve) => setTimeout(resolve, 500));

            const downloadParams = {
                start_date: startYmd,
                end_date: endYmd,
                outlet_id: filterParams.outlet || selectedOutlet,
                user_id: userId,
            };

            if (filterParams.main_category) {
                downloadParams.main_category = String(filterParams.main_category);
            }

            const createResponse = await createPluReportDownload(downloadParams);

            const directUrl =
                createResponse?.data?.url ||
                createResponse?.url ||
                createResponse?.data?.data?.url;

            if (directUrl) {
                await downloadFileWithAuth(
                    directUrl,
                    getDownloadFilename(createResponse, directUrl, 'plu-report.csv')
                );
                toast.success('Downloaded successfully');
                cleanup();
                setDownloadLoading(false);
                return;
            }

            const responseMessage =
                createResponse?.data?.data?.message ||
                createResponse?.data?.message ||
                createResponse?.message ||
                '';

            const isSuccess =
                createResponse?.data?.data?.success ||
                createResponse?.data?.success ||
                createResponse?.success ||
                createResponse?.data?.status === 'processing' ||
                createResponse?.data?.status === 'success';

            const isGeneratingMessage =
                typeof responseMessage === 'string' &&
                responseMessage.toLowerCase().includes('export is being generated');

            if (isSuccess || isGeneratingMessage) {
                return;
            }

            toast.error(responseMessage || 'Failed to initiate download');
            cleanup();
            setDownloadLoading(false);
        } catch (error) {
            console.error('Error downloading PLU report:', error);
            toast.error(error?.message || 'An error occurred while exporting PLU report');
            cleanup();
            setDownloadLoading(false);
        }
    };

    return (
        <div className={styles.pluReportContainer}>
            <ReportHeader
                outletName={outletName}
                periodText={periodText}
                categoryText={mainCategoryLabel}
                onDownload={handleDownloadCSV}
                downloadLoading={downloadLoading}
                hasData={hasData}
                downloadButtonText="Download CSV"
            />

            {loading ? (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            ) : !hasGenerated ? (
                <div className={styles.noData}>
                    <Placeholder
                        icon={<SearchNotFoundIcon width="24" height="24" color="white" />}
                        contentClassName={styles.placeholderContent}
                        heading="Please generate to see PLU report"
                        para={dataLagNotice || getDefaultDataLagNotice()}
                    />
                </div>
            ) : hasData || hasGifts ? (
                <div className={styles.content}>
                    {hasData && (
                    <div className={styles.tableWrapper}>
                        <table className={styles.pluTable}>
                            <thead>
                                <tr>
                                    <th className={`${styles.colPlu} ${styles.leftAlign}`}>PLU Number</th>
                                    <th className={`${styles.colName} ${styles.leftAlign}`}>Name</th>
                                    <th className={`${styles.colSold} ${styles.rightAlign}`}>Sold</th>
                                    <th className={`${styles.colMoney} ${styles.rightAlign}`}>Item sales</th>
                                    <th className={`${styles.colMoney} ${styles.rightAlign}`}>TotDisc</th>
                                    <th className={`${styles.colMoney} ${styles.rightAlign}`}>FOC</th>
                                    <th className={`${styles.colMoney} ${styles.rightAlign}`}>Revenue</th>
                                    <th className={`${styles.colSold} ${styles.rightAlign}`}>Refund</th>
                                    <th className={`${styles.colMoney} ${styles.rightAlign}`}>Refund Amt</th>
                                    <th className={`${styles.colSold} ${styles.rightAlign}`}>Nett Sold</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categories.map((cat, catIndex) => {
                                    const catKey = cat.category || cat.label || `cat-${catIndex}`;
                                    const catLabel = cat.label || cat.category || 'N/A';
                                    const items = Array.isArray(cat.items) ? cat.items : [];

                                    return (
                                        <React.Fragment key={catKey}>
                                            <tr className={styles.sectionHeaderRow}>
                                                <td colSpan={10}>{catLabel}</td>
                                            </tr>
                                            {items.map((item, itemIndex) => (
                                                <tr key={`${catKey}-${item.plu_number || itemIndex}`}>
                                                    <td className={`${styles.colPlu} ${styles.leftAlign}`}>
                                                        {item.plu_number || '-'}
                                                    </td>
                                                    <td className={`${styles.colName} ${styles.leftAlign}`}>
                                                        {item.item_name || 'N/A'}
                                                    </td>
                                                    {renderMetricCells(item)}
                                                </tr>
                                            ))}
                                            <tr className={styles.totalRow}>
                                                <td className={styles.leftAlign} colSpan={2}>
                                                    Total
                                                </td>
                                                {renderMetricCells(cat.total)}
                                            </tr>
                                        </React.Fragment>
                                    );
                                })}

                                <tr className={styles.summaryRow}>
                                    <td className={styles.leftAlign} colSpan={2}>
                                        GTotal
                                    </td>
                                    {renderMetricCells(gTotal)}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    )}
                    {hasGifts && (
                        <div className={styles.giftSection}>
                            <div className={styles.giftTitle}>Membership Gift Counts</div>
                            <div className={styles.giftTableWrap}>
                                <table className={styles.giftTable}>
                                    <thead>
                                        <tr>
                                            <th className={styles.leftAlign}>Gift</th>
                                            <th className={styles.leftAlign}>Category</th>
                                            <th className={styles.leftAlign}>Type</th>
                                            <th className={styles.rightAlign}>Count</th>
                                            <th className={styles.rightAlign}>Returned</th>
                                            <th className={styles.rightAlign}>Nett</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {membershipGifts.map((gift, index) => (
                                            <tr key={gift.gift_id ?? `${gift.gift_name}-${index}`}>
                                                <td className={styles.leftAlign}>{gift.gift_name || '-'}</td>
                                                <td className={styles.leftAlign}>{gift.gift_category || '-'}</td>
                                                <td className={styles.leftAlign}>
                                                    {gift.gift_type_label || gift.gift_type || '-'}
                                                </td>
                                                <td className={styles.rightAlign}>{formatQty(gift.quantity)}</td>
                                                <td className={styles.rightAlign}>{formatQty(gift.returned_quantity)}</td>
                                                <td className={styles.rightAlign}>{formatQty(gift.nett_quantity)}</td>
                                            </tr>
                                        ))}
                                        <tr className={styles.totalRow}>
                                            <td className={styles.centerAlign} colSpan={3}>
                                                Total
                                            </td>
                                            <td className={styles.rightAlign}>{giftTotals.quantity}</td>
                                            <td className={styles.rightAlign}>{giftTotals.returned_quantity}</td>
                                            <td className={styles.rightAlign}>{giftTotals.nett_quantity}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className={styles.noData}>
                    <Placeholder
                        icon={<SearchNotFoundIcon width="24" height="24" color="white" />}
                        contentClassName={styles.placeholderContent}
                        heading="No PLU report data found"
                        para={
                            <>
                                {dataLagNotice || getDefaultDataLagNotice()}
                                <br />
                                <br />
                                Please try changing the filters
                            </>
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default PluReport;
